/* eslint-disable react/jsx-no-bind */
import { ContentCopy, Visibility, Search, Clear } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  useTheme,
  Divider,
  CircularProgress,
  TextField,
  Grid,
  InputAdornment,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import { tokens } from '../../theme';
import { formatToIST } from '../../utils/dateFormatter';
import logger from '../../utils/logger';

import financeService from './financeService';

const Finance = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [payoutData, setPayoutData] = useState([]);
  const [refundData, setRefundData] = useState([]);
  const [filteredPayoutData, setFilteredPayoutData] = useState([]);
  const [filteredRefundData, setFilteredRefundData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const copyToClipboard = (text, type) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(`${type} copied to clipboard!`);
      })
      .catch(() => {
        toast.error('Failed to copy to clipboard');
      });
  };

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const response = await financeService.fetchPayouts();
      if (response.data.status) {
        const data = response.data.data.result || [];
        setPayoutData(data);
        setFilteredPayoutData(data);
      }
    } catch (error) {
      toast.error('Error fetching payouts');
      logger.error('Error fetching payouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRefunds = async () => {
    setLoading(true);
    try {
      const response = await financeService.fetchRefunds();
      if (response.data.status) {
        const data = response.data.data.result || [];
        setRefundData(data);
        setFilteredRefundData(data);
      }
    } catch (error) {
      toast.error('Error fetching refunds');
      logger.error('Error fetching refunds:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePayoutSummary = () => {
    const totalPayouts = filteredPayoutData.reduce(
      (sum, item) => sum + (item.payoutAmount || 0),
      0,
    );
    const totalCommission = filteredPayoutData.reduce(
      (sum, item) => sum + (item.commission || 0),
      0,
    );
    const totalFare = filteredPayoutData.reduce(
      (sum, item) => sum + (item.totalFare || 0),
      0,
    );
    const completedPayouts = filteredPayoutData.filter(
      (item) => item.status?.toLowerCase() === 'received',
    ).length;

    return {
      totalPayouts: totalPayouts.toFixed(2),
      totalCommission: totalCommission.toFixed(2),
      totalFare: totalFare.toFixed(2),
      completedPayouts,
      totalCount: filteredPayoutData.length,
    };
  };

  const calculateRefundSummary = () => {
    const totalRefunds = filteredRefundData.reduce(
      (sum, item) => sum + (item.refundAmount || 0),
      0,
    );
    const totalOriginal = filteredRefundData.reduce(
      (sum, item) => sum + (item.originalAmount || 0),
      0,
    );
    const totalCommissionLost = filteredRefundData.reduce(
      (sum, item) => sum + (item.commissionLost || 0),
      0,
    );
    const processedRefunds = filteredRefundData.filter(
      (item) => item.paymentStatus?.toLowerCase() === 'refunded',
    ).length;

    return {
      totalRefunds: totalRefunds.toFixed(2),
      totalOriginal: totalOriginal.toFixed(2),
      totalCommissionLost: totalCommissionLost.toFixed(2),
      processedRefunds,
      totalCount: filteredRefundData.length,
    };
  };

  const SummaryCard = ({
    title,
    value,
    subtitle,
    color = colors.swiftcarry[100],
  }) => (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      }}
    >
      <CardContent sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color, mb: 1 }}>
          {value}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (activeTab === 0) {
      const filtered = payoutData.filter((item) =>
        item.userName?.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredPayoutData(filtered);
    } else {
      const filtered = refundData.filter((item) =>
        item.userName?.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredRefundData(filtered);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    if (activeTab === 0) {
      setFilteredPayoutData(payoutData);
    } else {
      setFilteredRefundData(refundData);
    }
  };

  useEffect(() => {
    if (activeTab === 0) {
      fetchPayouts();
    } else {
      fetchRefunds();
    }
  }, [activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchTerm('');
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'received':
      case 'processed':
      case 'refunded':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'failed':
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Card
      sx={{
        boxShadow: 2,
        borderRadius: 2,
        padding: 1,
        margin: 2,
        marginLeft: 4,
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingLeft: 4,
            paddingTop: 2,
            paddingBottom: 0,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Finance Management
          </Typography>
        </Box>
        <Divider sx={{ my: 2 }} />

        {/* Tabs */}
        <Box sx={{ padding: 4 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              mb: 3,
              '& .MuiTabs-indicator': {
                backgroundColor: colors.swiftcarry[100],
                height: 3,
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '16px',
                '&.Mui-selected': {
                  color: colors.swiftcarry[100],
                },
              },
            }}
          >
            <Tab label="Payouts" />
            <Tab label="Refunds" />
          </Tabs>

          {/* Search */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Search by Name"
                variant="outlined"
                size="small"
                fullWidth
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search by traveler/customer name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 20, color: 'gray' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        sx={{ padding: '4px' }}
                      >
                        <Clear sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '8px',
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? colors.primary[400]
                        : 'white',
                    '&.Mui-focused fieldset': {
                      borderColor: colors.swiftcarry[100],
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'gray',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: colors.swiftcarry[100],
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Payouts Tab */}
          {activeTab === 0 && (
            <Box>
              <Typography
                variant="h6"
                sx={{ mb: 3, fontWeight: 600, color: colors.swiftcarry[100] }}
              >
                Traveler Payouts
              </Typography>

              {/* Payout Summary */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryCard
                    title="Total Payouts"
                    value={`₹${calculatePayoutSummary().totalPayouts}`}
                    subtitle="Amount paid to travelers"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryCard
                    title="Total Commission"
                    value={`₹${calculatePayoutSummary().totalCommission}`}
                    subtitle="Platform earnings"
                    color="#ff9800"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryCard
                    title="Total Fare"
                    value={`₹${calculatePayoutSummary().totalFare}`}
                    subtitle="Gross revenue"
                    color="#2196f3"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryCard
                    title="Completed"
                    value={`${calculatePayoutSummary().completedPayouts}/${
                      calculatePayoutSummary().totalCount
                    }`}
                    subtitle="Successful payouts"
                    color="#4caf50"
                  />
                </Grid>
              </Grid>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Payout ID
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Traveler
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Total Fare
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Commission
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Payout Amount
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Status
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Transfer ID
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Created Date
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredPayoutData.length > 0 ? (
                          filteredPayoutData.map((row) => (
                            <TableRow key={row.payoutId}>
                              <TableCell>{row.payoutId}</TableCell>
                              <TableCell>
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {row.userName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {row.userPhone}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>₹{row.totalFare}</TableCell>
                              <TableCell>₹{row.commission}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>
                                ₹{row.payoutAmount}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={row.status}
                                  color={getStatusColor(row.status)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: 'monospace' }}
                                  >
                                    {row.transferId}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      copyToClipboard(
                                        row.transferId,
                                        'Transfer ID',
                                      )
                                    }
                                    sx={{ p: 0.5 }}
                                  >
                                    <ContentCopy sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {row.createdOn
                                  ? formatToIST(row.createdOn)
                                  : 'N/A'}
                              </TableCell>
                              <TableCell>
                                <IconButton size="small">
                                  <Visibility sx={{ fontSize: 18 }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={9} align="center">
                              {searchTerm
                                ? 'No payouts found matching search'
                                : 'No payouts found'}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}

          {/* Refunds Tab */}
          {activeTab === 1 && (
            <Box>
              <Typography
                variant="h6"
                sx={{ mb: 3, fontWeight: 600, color: colors.swiftcarry[100] }}
              >
                Customer Refunds
              </Typography>

              {/* Refund Summary */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryCard
                    title="Total Refunds"
                    value={`₹${calculateRefundSummary().totalRefunds}`}
                    subtitle="Amount refunded"
                    color="#f44336"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryCard
                    title="Original Amount"
                    value={`₹${calculateRefundSummary().totalOriginal}`}
                    subtitle="Total booking value"
                    color="#2196f3"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryCard
                    title="Commission Lost"
                    value={`₹${calculateRefundSummary().totalCommissionLost}`}
                    subtitle="Revenue impact"
                    color="#ff9800"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryCard
                    title="Processed"
                    value={`${calculateRefundSummary().processedRefunds}/${
                      calculateRefundSummary().totalCount
                    }`}
                    subtitle="Completed refunds"
                    color="#4caf50"
                  />
                </Grid>
              </Grid>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Refund ID
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Customer
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Original Amount
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Refund Amount
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Commission Lost
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Refund Status
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Payment Status
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Razorpay Refund ID
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Requested Date
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredRefundData.length > 0 ? (
                          filteredRefundData.map((row) => (
                            <TableRow key={row.refundId}>
                              <TableCell>{row.refundId}</TableCell>
                              <TableCell>
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {row.userName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {row.userPhone}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>₹{row.originalAmount}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>
                                ₹{row.refundAmount}
                              </TableCell>
                              <TableCell>₹{row.commissionLost}</TableCell>
                              <TableCell>
                                <Chip
                                  label={row.refundStatus}
                                  color={getStatusColor(row.refundStatus)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={row.paymentStatus}
                                  color={getStatusColor(row.paymentStatus)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: 'monospace' }}
                                  >
                                    {row.razorpayRefundId}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      copyToClipboard(
                                        row.razorpayRefundId,
                                        'Razorpay Refund ID',
                                      )
                                    }
                                    sx={{ p: 0.5 }}
                                  >
                                    <ContentCopy sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {row.requestedAt
                                  ? formatToIST(row.requestedAt)
                                  : 'N/A'}
                              </TableCell>
                              <TableCell>
                                <IconButton size="small">
                                  <Visibility sx={{ fontSize: 18 }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={10} align="center">
                              {searchTerm
                                ? 'No refunds found matching search'
                                : 'No refunds found'}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default Finance;
